import React, { useState, useEffect, useMemo } from "react";
import { X, Search, User, Building, Mail, Plus } from "lucide-react";
import { useLeads } from "../../../context/LeadContext";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import { handleSearchInputChange, FIELD_LIMITS } from "../../../utils/inputValidation";
import { toast } from "../../../utils/toast";

const LeadSelectionModal = ({ isOpen, onClose, onSelect }) => {
  const {
    leads,
    loading: leadsLoading,
    loadLeads,
    pagination: leadsPagination,
  } = useLeads();

  const [leadSearchTerm, setLeadSearchTerm] = useState("");
  const [debouncedLeadSearchTerm, setDebouncedLeadSearchTerm] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState("");
  const [leadPage, setLeadPage] = useState(1);
  const [leadLimit, setLeadLimit] = useState(10);

  // Reset pagination when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setLeadPage(1);
      setLeadSearchTerm("");
      setDebouncedLeadSearchTerm("");
      setSelectedLeadId("");
    }
  }, [isOpen]);

  // Debounce lead search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLeadSearchTerm(leadSearchTerm);
      setLeadPage(1); // Reset page when search term changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [leadSearchTerm]);

  // Load leads when the lead selection modal opens or search term changes
  useEffect(() => {
    if (isOpen) {
      // Build filters for backend search
      const filters = {
        page: leadPage,
        limit: leadLimit,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      // Add search filter if provided
      if (debouncedLeadSearchTerm && debouncedLeadSearchTerm.trim()) {
        filters.search = debouncedLeadSearchTerm.trim();
      }

      loadLeads(filters);
    }
  }, [
    isOpen,
    debouncedLeadSearchTerm,
    leadPage,
    leadLimit,
    loadLeads,
  ]);

  // Helper functions
  const getLeadName = (lead) => {
    if (!lead) return "Unknown Lead";
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return "Unknown Lead";
    }
  };

  // Use leads directly from context (already filtered by backend search)
  // Sort by createdAt (newest first) for display
  const displayedLeads = useMemo(() => {
    if (!leads || leads.length === 0) return [];

    // Sort by createdAt (newest first)
    const sorted = [...leads].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || 0);
      return dateB - dateA; // Descending order (newest first)
    });

    return sorted;
  }, [leads]);

  // Handle lead selection and navigate/callback to log activity form
  const handleSelectLeadAndLog = () => {
    if (selectedLeadId) {
      onSelect(selectedLeadId);
    } else {
      toast.warning("Please select a lead to log activity");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Select Lead</h2>
            <p className="text-gray-600 text-sm mt-1">
              Choose a lead to log activity for
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads by name, company, or email..."
              value={leadSearchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(
                  e,
                  setLeadSearchTerm,
                  FIELD_LIMITS.search,
                );
                setLeadSearchTerm(sanitized);
              }}
              maxLength={FIELD_LIMITS.search}
              className="input-field pl-10"
            />
          </div>
        </div>

        {/* Leads List - Scrollable, showing max 3 items at a time */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ maxHeight: "calc(3 * (120px + 0.5rem))" }}
        >
          {leadsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                <p className="text-gray-600">Loading leads...</p>
              </div>
            </div>
          ) : displayedLeads.length === 0 ? (
            <div className="text-center py-12">
              <div className="p-3 bg-gray-100 rounded-full mb-4 inline-block">
                <User className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No leads found
              </h3>
              <p className="text-gray-500">
                {leadSearchTerm
                  ? "Try adjusting your search criteria."
                  : "No leads available. Please add leads first."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedLeads.map((lead) => {
                const leadId = lead.id || lead._id || lead.leadId;
                const isSelected = selectedLeadId === leadId;

                return (
                  <div
                    key={leadId}
                    onClick={() => {
                      // Toggle selection: if already selected, unselect it
                      setSelectedLeadId(isSelected ? "" : leadId);
                    }}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors overflow-hidden ${
                      isSelected
                        ? "border-agrosaf-green bg-agrosaf-green/10"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 overflow-hidden">
                      <div className="flex items-start space-x-3 flex-1 min-w-0 overflow-hidden">
                        <div
                          className={`p-2 rounded-lg flex-shrink-0 ${
                            isSelected
                              ? "bg-agrosaf-green text-white"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          <User className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <h4
                            className="font-semibold text-gray-900 truncate block capitalize"
                            title={getLeadName(lead)}
                          >
                            {getLeadName(lead)}
                          </h4>
                          {lead.company && (
                            <div className="flex items-center space-x-1 text-sm text-gray-600 mt-1 min-w-0 overflow-hidden">
                              <Building className="h-4 w-4 flex-shrink-0" />
                              <span
                                className="truncate block min-w-0 first-letter:capitalize"
                                title={lead.company}
                              >
                                {lead.company}
                              </span>
                            </div>
                          )}
                          {lead.email && (
                            <div className="flex items-center space-x-1 text-sm text-gray-500 mt-2 min-w-0 overflow-hidden">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span
                                className="truncate block min-w-0 lowercase"
                                title={lead.email}
                              >
                                {lead.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="ml-2 flex-shrink-0">
                          <div className="w-5 h-5 rounded-full bg-agrosaf-green flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Lead Pagination */}
        <div className="px-6 pb-4">
          <PaginationControls
            currentPage={leadsPagination.currentPage || 1}
            totalPages={leadsPagination.totalPages || 1}
            totalItems={leadsPagination.totalLeads || 0}
            limit={leadsPagination.limit || 5}
            hasNextPage={leadsPagination.hasNextPage}
            hasPrevPage={leadsPagination.hasPrevPage}
            onPageChange={(page) => setLeadPage(page)}
            onLimitChange={(limit) => {
              setLeadLimit(limit);
              setLeadPage(1);
            }}
            limitOptions={[5, 10, 20, 50, 100]}
          />
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center space-x-3 p-6 border-t bg-gray-50">
          <button
            className="btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn-create flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSelectLeadAndLog}
            disabled={!selectedLeadId}
          >
            <Plus className="h-4 w-4" />
            <span>Log Activity</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadSelectionModal;
